import { useTranslation } from 'react-i18next';
import { Star, ExternalLink } from 'lucide-react';
import ImpressionTracker from './ImpressionTracker';

const API_URL = '';

const TIER_MAP = {
  nl: { best: 'Beste', better: 'Beter', good: 'Goed' },
  en: { best: 'Best', better: 'Better', good: 'Good' },
  de: { best: 'Beste', better: 'Besser', good: 'Gut' },
  fr: { best: 'Meilleur', better: 'Mieux', good: 'Bon' },
};

const TIER_STYLE = {
  best: 'bg-green-100 text-green-700 border-green-200',
  better: 'bg-blue-100 text-blue-700 border-blue-200',
  good: 'bg-muted text-muted-foreground border-border',
};

function trackClick(product, reportId, country, region) {
  fetch(`${API_URL}/api/track/click`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_id: reportId || '',
      product_id: product.product_id,
      category: product.category || '',
      affiliate_link: product.affiliate_link || '',
      country: country || '',
      region: region || '',
    }),
  }).catch(() => {});
}

export default function StandaloneProducts({ products, reportId, country, region, onImpression }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  if (!products || products.length === 0) return null;

  const tierLabels = TIER_MAP[lang] || TIER_MAP.en;
  const viewLabel = lang === 'nl' ? 'Bekijk' : lang === 'de' ? 'Ansehen' : lang === 'fr' ? 'Voir' : 'View';
  const sourceLabel = lang === 'nl' ? 'Bron' : 'Source';

  return (
    <div className="bg-white rounded-xl border border-border shadow-sm p-5" data-testid="standalone-products">
      <h3 className="text-base font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>
        {t('report.additionalSavings')}
      </h3>
      <div className="space-y-2">
        {products.map(p => (
          <ImpressionTracker key={p.product_id} productId={p.product_id} category={p.category} onImpression={onImpression}>
            <div className="flex items-center justify-between gap-3 text-sm bg-muted/30 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase border flex-shrink-0 ${TIER_STYLE[p.tier] || TIER_STYLE.good}`}>
                  {tierLabels[p.tier] || p.tier}
                </span>
                <div className="min-w-0">
                  <span className="font-medium block truncate">{p.product_name}</span>
                  <span className="text-xs text-muted-foreground">{p.manufacturer} · {p.category?.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {p.review_score && (
                  <span className="flex items-center gap-0.5 text-amber-600 text-xs">
                    <Star className="w-3 h-3 fill-amber-400" />
                    {p.review_score}
                  </span>
                )}
                {p.retail_price_eur && (
                  <span className="text-xs text-muted-foreground">€{p.retail_price_eur.toLocaleString()}</span>
                )}
                {p.affiliate_link && (
                  <a href={p.affiliate_link} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackClick(p, reportId, country, region)}
                    className="text-primary hover:underline flex items-center gap-0.5 text-xs">
                    {viewLabel} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </ImpressionTracker>
        ))}
      </div>
      {products[0]?.source_name && (
        <p className="text-[10px] text-muted-foreground mt-1.5">{sourceLabel}: {products[0].source_name}</p>
      )}
    </div>
  );
}
